<template>
  <div class="finetune-detail flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        Finetune Suggestion Status
      </span>
    </div>

    <div v-if="loading" class="d-flex justify-content-center align-items-center pt-35-px flex-grow-1">
      <Spinner />
    </div>

    <template v-else>
      <div class="finetune-detail__content flex-grow-1 overflow-y-auto">
        <div class="finetune-detail__summary px-20 pt-3">
          <a-descriptions bordered size="small" :column="{ xs: 1, sm: 2 }" class="mb-3">
            <a-descriptions-item label="Job ID">
              {{ detail.job_id || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="Status">
              <a-tag :color="statusTagColor">
                {{ displayStatus }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="Analysis window">
              {{ analysisWindowLabel }}
            </a-descriptions-item>
            <a-descriptions-item label="Created">
              <template v-if="detail.created_at">
                {{ formatDetailDateTime(detail.created_at) }}
              </template>
              <template v-else>
                —
              </template>
            </a-descriptions-item>
            <a-descriptions-item label="Completed">
              <template v-if="detail.completed_at">
                {{ formatDetailDateTime(detail.completed_at) }}
              </template>
              <template v-else>
                —
              </template>
            </a-descriptions-item>
            <a-descriptions-item label="Alert Threshold">
              {{ detail.threshold_alert != null ? detail.threshold_alert : '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="Frequency threshold">
              {{ formatPercentage(detail.frequency_threshold) }}
            </a-descriptions-item>
            <a-descriptions-item label="Total URLs">
              {{ isSuggestionReady ? detail.total_urls : '—' }}
            </a-descriptions-item>
          </a-descriptions>
        </div>

        <div v-if="isInProgress" class="px-20 pb-3">
          <div class="finetune-loading-state">
            <a-icon type="loading" class="finetune-loading-state__spinner" spin />
            <p class="finetune-loading-state__title">
              Finetune analysis in progress
            </p>
            <p class="finetune-loading-state__subtitle">
              We’re analyzing your data and preparing recommendations.
            </p>
          </div>
        </div>

        <div v-else-if="isFailed" class="px-20 pb-3">
          <a-alert
            message="Finetune analysis failed"
            description="No suggestion result was produced for this job."
            type="error"
            show-icon
          />
        </div>

        <div v-else-if="isCompleted && !isSuggestionReady" class="px-20 pb-3">
          <a-alert
            message="Suggestion result unavailable"
            description="The job completed, but its suggestion result could not be loaded."
            type="warning"
            show-icon
          />
        </div>

        <div v-if="isSuggestionReady" class="px-20 pb-3">
          <a-card size="small" :bordered="false">
            <template slot="title">
              <span>Suggestion review</span>
            </template>

            <a-alert
              type="info"
              show-icon
              class="mb-3"
              :message="suggestionSummary.title"
            >
              <template slot="description">
                <div>
                  <strong>How suggestions are derived:</strong>
                  <br>
                  - URLs are analyzed after meeting the Alert Threshold. <br>
                  - Recurring content-count changes and integrity changes must also meet the Frequency threshold within the
                  analysis window before becoming threshold or do-not-monitor suggestions.
                </div>
                <div class="mt-1">
                  <strong>Why some suggestions are unchecked:</strong>
                  <br>
                  Thresholds above half of an affected URL’s baseline and broad content-level
                  change assets such as Internal_CSS_#, In-line_JS_#, inline images, or iframes are
                  unchecked by default. For content-level change assets, review the affected page’s
                  code and finetune it with a specific rule. These suggestions can still be
                  selected manually.
                </div>
                <div class="mt-1">
                  <strong>Important:</strong>
                  Finetune suggestions are generated automatically and should be treated as
                  recommendations. Double-check every adjustment against the affected page and
                  expected monitoring behavior before applying it.
                </div>
              </template>
            </a-alert>

            <a-row :gutter="16">
              <a-col :xs="24" :md="8">
                <a-statistic title="Threshold Suggestions" :value="suggestionSummary.thresholdSuggestionCount" />
                <div class="text-muted">
                  across {{ suggestionSummary.thresholdUrlsAffected }} affected URL{{ suggestionSummary.thresholdUrlsAffected === 1 ? '' : 's' }}
                </div>
              </a-col>
              <a-col :xs="24" :md="8">
                <a-statistic title="Do-not-monitor items" :value="suggestionSummary.policyRuleCount" />
                <div class="text-muted">
                  across {{ suggestionSummary.policyUrlsCovered }} URL{{ suggestionSummary.policyUrlsCovered === 1 ? '' : 's' }} with integrity suggestions
                </div>
              </a-col>
              <a-col :xs="24" :md="8">
                <a-statistic title="Coverage" :value="suggestionSummary.coverageCount" :suffix="`/ ${suggestionSummary.coverageTotal} URLs`" />
                <div class="text-muted">
                  Primary focus: {{ suggestionSummary.topFocus }}
                </div>
              </a-col>
            </a-row>
          </a-card>
        </div>

        <div v-if="isSuggestionReady" class="finetune-detail__tabs">
          <a-tabs :activeKey="activeTabKey" class="finetune-detail__main-tabs" @change="onTabChange">
            <a-tab-pane key="threshold" tab="Threshold" :forceRender="true">
              <FinetuneSuggestionDetailThreshold :detail="detail" />
            </a-tab-pane>
            <a-tab-pane key="policy" tab="Do not monitor" :forceRender="true">
              <FinetuneSuggestionDetailPolicy :detail="detail" />
            </a-tab-pane>
          </a-tabs>
        </div>
      </div>
    </template>

    <div class="a-footer">
      <a-button size="large" class="slide-down-enter-active">
        <router-link :to="{ name: autoFinetuneRouteNames.SUGGESTIONS }">
          Back to Suggestions
        </router-link>
      </a-button>
    </div>
  </div>
</template>

<script>
import {
  formatAccountDateTime,
  formatAnalysisWindow,
  resolveAccountTimezone
} from '@modules/AutoFinetune/helpers/dateTime';
import { fetchAutoFinetuneMetadata } from '@modules/AutoFinetune/helpers/metadata';
import { formatPercentage } from '@modules/AutoFinetune/helpers/percentage';
import {
  doesJobStatusLoadSuggestion,
  getJobStatusColor,
  getJobStatusLabel,
  isJobStatusFailure,
  isJobStatusPollable
} from '@modules/AutoFinetune/helpers/status';
import { AUTO_FINETUNE_ROUTE_NAMES } from '@modules/AutoFinetune/router/routes';
import { countGroupedRules } from '@shared/utils/policyRules';
import { getApiErrorMessage } from '@shared/utils/finetune';
import FinetuneSuggestionService from '../services/FinetuneSuggestion.service';
import FinetuneSuggestionDetailThreshold from './FinetuneSuggestionDetailThreshold.vue';
import FinetuneSuggestionDetailPolicy from './FinetuneSuggestionDetailPolicy.vue';

const DETAIL_POLL_INTERVAL_MS = 5000;

const createEmptyDetail = () => ({
  job_id: null,
  cust_id: null,
  status: null,
  start_datetime: null,
  end_datetime: null,
  created_at: null,
  completed_at: null,
  analysis_start_date: '',
  suggestion_ready: false,
  threshold_alert: null,
  frequency_threshold: null,
  total_urls: 0,
  urls: [],
  policy_suggestions: []
});

export default {
  name: 'FinetuneSuggestionDetail',
  components: {
    FinetuneSuggestionDetailThreshold,
    FinetuneSuggestionDetailPolicy
  },
  data() {
    return {
      autoFinetuneRouteNames: AUTO_FINETUNE_ROUTE_NAMES,
      activeTabKey: 'threshold',
      loading: true,
      metadata: null,
      detail: createEmptyDetail(),
      pollTimerId: null,
      isDestroyed: false
    };
  },
  computed: {
    customerTimezone() {
      return resolveAccountTimezone(this.$store);
    },
    analysisWindowLabel() {
      return formatAnalysisWindow(
        this.detail.start_datetime,
        this.detail.end_datetime,
        this.customerTimezone
      );
    },
    displayStatus() {
      return getJobStatusLabel(this.metadata, this.detail.status);
    },
    statusTagColor() {
      return getJobStatusColor(this.metadata, this.detail.status);
    },
    isCompleted() {
      return doesJobStatusLoadSuggestion(this.metadata, this.detail.status);
    },
    isFailed() {
      return isJobStatusFailure(this.metadata, this.detail.status);
    },
    isInProgress() {
      return isJobStatusPollable(this.metadata, this.detail.status);
    },
    isSuggestionReady() {
      return !!this.detail.suggestion_ready;
    },
    suggestionSummary() {
      const urls = Array.isArray(this.detail.urls) ? this.detail.urls : [];
      const policyGroups = Array.isArray(this.detail.policy_suggestions)
        ? this.detail.policy_suggestions
        : [];

      const thresholdUrls = urls.filter((url) => (url.suggestions || []).length > 0);
      const thresholdUrlsAffected = thresholdUrls.length;
      const thresholdSuggestionCount = thresholdUrls.reduce(
        (total, url) => total + url.suggestions.length,
        0
      );
      const policyRuleCount = policyGroups.reduce((total, group) => {
        const rules =
          group.mode === 'existing' ? group.newRulesOnly || [] : group.rules || [];
        return total + countGroupedRules(rules);
      }, 0);
      const policyUrlsCovered = policyGroups.reduce((total, group) => total + ((group.urls || []).length || 0), 0);

      const thresholdElementCount = {};
      urls.forEach((url) => {
        (url.suggestions || []).forEach((suggestion) => {
          thresholdElementCount[suggestion.element] = (thresholdElementCount[suggestion.element] || 0) + 1;
        });
      });

      const topThresholdElement = this.getTopCategoryLabel(
        thresholdElementCount,
        {
          link: 'CSS threshold changes',
          div: 'DIV threshold changes',
          href: 'Href threshold changes',
          img: 'Image threshold changes',
          line_num: 'Line-count threshold changes',
          script: 'Script threshold changes',
          iframe: 'Iframe threshold changes',
          form: 'Form threshold changes'
        },
        'threshold tuning'
      );
      const combinedCount = thresholdSuggestionCount + policyRuleCount;
      const policyUrlIds = policyGroups.reduce((allIds, group) => {
        return allIds.concat(group.urlIds || []);
      }, []);
      const uniqueCoverageCount = new Set([
        ...thresholdUrls.map((url) => url.urlID),
        ...policyUrlIds
      ]).size;
      const coverageTotal = this.detail.total_urls || urls.length || 0;

      return {
        title:
          combinedCount > 0
            ? `${combinedCount} suggestions ready for review`
            : 'No threshold or do-not-monitor suggestions available',
        coverageCount: uniqueCoverageCount,
        coverageTotal,
        thresholdSuggestionCount,
        thresholdUrlsAffected,
        policyRuleCount,
        policyUrlsCovered,
        topFocus: topThresholdElement
      };
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.stopPolling();
        this.loadDetail();
      }
    }
  },
  mounted() {
    this.loadMetadata();
    this.loadDetail();
  },
  beforeDestroy() {
    this.isDestroyed = true;
    this.stopPolling();
  },
  methods: {
    async loadMetadata() {
      try {
        this.metadata = await fetchAutoFinetuneMetadata();
        if (this.isInProgress) this.schedulePoll();
      } catch (error) {
        this.metadata = null;
        this.stopPolling();
      }
    },
    onTabChange(activeKey) {
      this.activeTabKey = activeKey;
    },
    formatDetailDateTime(value) {
      return formatAccountDateTime(value, this.customerTimezone);
    },
    formatPercentage,
    getTopCategoryLabel(countMap, labelMap, fallback) {
      const entries = Object.entries(countMap);
      if (!entries.length) return fallback;

      const [label] = entries.reduce((top, entry) => (entry[1] > top[1] ? entry : top));
      return labelMap[label] || label;
    },
    stopPolling() {
      if (this.pollTimerId != null) {
        clearTimeout(this.pollTimerId);
        this.pollTimerId = null;
      }
    },
    schedulePoll() {
      this.stopPolling();
      if (this.isDestroyed || !this.isInProgress) return;
      this.pollTimerId = setTimeout(() => {
        this.loadDetail({ silent: true });
      }, DETAIL_POLL_INTERVAL_MS);
    },
    normalizeFinetuneDetailPayload(response) {
      const job = (response && response.job) || {};
      const rawSuggestion = response && response.suggestion;
      const data = rawSuggestion || {};
      const rawUrls = Array.isArray(data.url_content_engine_threshold_suggestions)
        ? data.url_content_engine_threshold_suggestions
        : [];
      const urls = rawUrls.map((u) => ({
        ...u,
        suggestions: Array.isArray(u.content_engine_threshold_suggestions)
          ? u.content_engine_threshold_suggestions.map((s) => ({
              ...s,
              element: s.element === 'css' ? 'link' : s.element
            }))
          : []
      }));

      const autoPolicySuggestion = data.auto_policy_suggestion || {};
      const policy_suggestions = Array.isArray(autoPolicySuggestion.groups)
        ? autoPolicySuggestion.groups
            .filter((group) => {
              const selectableRules =
                group.mode === 'existing' ? group.newRulesOnly : group.rules;
              return Array.isArray(selectableRules) && selectableRules.length > 0;
            })
        : [];

      return {
        job_id: job.id,
        cust_id: job.customer_id,
        status: job.status,
        start_datetime: job.start_datetime,
        end_datetime: job.end_datetime,
        created_at: job.created_at,
        completed_at: job.completed_at,
        analysis_start_date: job.start_datetime
          ? formatAccountDateTime(
              job.start_datetime,
              this.customerTimezone,
              'YYYY-MM-DD',
              ''
            )
          : '',
        suggestion_ready: rawSuggestion != null,
        threshold_alert: data.threshold_alert,
        frequency_threshold: data.frequency_threshold,
        total_urls: data.total_urls != null ? data.total_urls : rawUrls.length,
        urls,
        policy_suggestions
      };
    },
    async loadDetail(options = {}) {
      const silent = !!options.silent;
      if (!silent) {
        this.loading = true;
      }
      const { job_id: jobId } = this.$route.query || {};
      if (!jobId) {
        this.stopPolling();
        this.detail = createEmptyDetail();
        this.loading = false;
        this.$message.warning('Select a suggestion job from the list.');
        return;
      }
      try {
        const data = await FinetuneSuggestionService.fetchJobDetail(jobId);
        const currentJobId = (this.$route.query && this.$route.query.job_id) || null;
        if (this.isDestroyed || String(currentJobId) !== String(jobId)) return;
        this.detail = this.normalizeFinetuneDetailPayload(data || {});
        if (this.isInProgress) {
          this.schedulePoll();
        } else {
          this.stopPolling();
        }
      } catch (e) {
        if (this.isDestroyed) return;
        if (!silent) {
          this.detail = createEmptyDetail();
          this.$message.error(
            getApiErrorMessage(e, 'Failed to load finetune suggestion.')
          );
        } else {
          this.schedulePoll();
        }
      } finally {
        if (!silent) {
          this.loading = false;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.px-20 {
  padding-left: 20px;
  padding-right: 20px;
}

.finetune-detail__content {
  min-height: 0;
}

.finetune-loading-state {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;

  &__spinner {
    color: #1890ff;
    font-size: 30px;
  }

  &__title {
    margin: 16px 0 4px;
    font-size: 16px;
    font-weight: 500;
  }

  &__subtitle {
    margin: 0;
    color: $prLgt;
  }
}

</style>
